import React, { useState, useEffect, useMemo, useRef } from "react";
import { Accordion, Col, Row } from "react-bootstrap";
import ReactPlayer from "react-player";
import defaultImage from "../../Assets/images/pp_Logo.jpg";

// styles
import styles from "./preview.module.scss";
import detailStyles from "../PlayerProfileDetail/detail.module.scss";
import directoryStyles from "../componentsPage/keyPlayerContactsDirectory/KeyPlayerContactsDirectory.module.scss";
import { DataVizProjectionChart } from "../PlayerProfileDetail/DataVizProjectionChart";
import { PlayerReports } from "./reports";
import { SelectCompareProfile } from "./select-compare-profile";

// icons
import {
  IoSwapHorizontalOutline,
  IoChevronBackCircleOutline,
  IoChevronBack,
  IoStarOutline,
  IoStarSharp,
  IoPrintOutline,
} from "react-icons/io5";

import moment from "moment";
import { formatFeetInches, formatMeasurementFeetInches } from "../../utils/feetInches";
import { isDisplayableIndustryRankValue, formatWeightDisplay } from "../../utils/helpers";
import { formatProfileAge } from "../../utils/age";
import { useHistory } from "react-router-dom";
import { auth, db, getStorageProxyUrlForPdf } from "../../firebase";
import { useAuth } from "../../Context/AuthContext";

import html2pdf from "html2pdf.js";
import { getStorage, ref, getBytes } from "firebase/storage";

import {
  BodyTitle,
  BodyColList,
  BodySocial,
  BodyList,
} from "./sections";

function getYouTubeVideoId(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return parsed.pathname.split("/").filter(Boolean)[0] || null;
    }

    if (host.endsWith("youtube.com")) {
      if (parsed.pathname === "/watch") {
        return parsed.searchParams.get("v");
      }
      if (parsed.pathname.startsWith("/embed/") || parsed.pathname.startsWith("/shorts/")) {
        return parsed.pathname.split("/").filter(Boolean)[1] || null;
      }
    }
  } catch (e) {
    // Fallback regex for malformed links.
    const match = String(url).match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
    );
    return match ? match[1] : null;
  }

  return null;
}

function guessImageMimeFromUrl(url) {
  const u = String(url).toLowerCase();
  if (u.includes("image%2Fpng") || u.includes(".png")) return "image/png";
  if (u.includes("image%2Fwebp") || u.includes(".webp")) return "image/webp";
  if (u.includes("image%2Fgif") || u.includes(".gif")) return "image/gif";
  return "image/jpeg";
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function bytesToDataUrl(bytes, mimeType) {
  return blobToDataUrl(new Blob([bytes], { type: mimeType }));
}

/**
 * Load remote headshot into a data URL so html2canvas can paint it without CORS/taint.
 * Order: (1) HTTPS proxy Cloud Function — Admin SDK download, no browser CORS;
 * (2) Storage getBytes — still uses XHR and fails without bucket CORS;
 * (3) fetch — needs CORS on the file response.
 */
async function fetchStorageImageViaProxy(url) {
  const endpoint =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_STORAGE_PROXY_URL) ||
    getStorageProxyUrlForPdf();
  if (!endpoint) return null;
  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      console.warn("[pdf] Storage proxy HTTP", res.status);
      return null;
    }
    const data = await res.json();
    if (!data || !data.base64) return null;
    const ct = data.contentType || guessImageMimeFromUrl(url);
    return `data:${ct};base64,${data.base64}`;
  } catch (e) {
    console.warn("[pdf] Storage proxy failed:", e);
    return null;
  }
}

async function resolveImageUrlToDataUrlForPdf(url) {
  if (!url || String(url).trim() === "") return null;
  const s = String(url).trim();
  if (s.startsWith("data:")) return s;

  const isFirebaseStorage =
    /^gs:\/\//i.test(s) ||
    /firebasestorage\.googleapis\.com/i.test(s) ||
    /storage\.googleapis\.com/i.test(s);

  if (isFirebaseStorage) {
    const proxied = await fetchStorageImageViaProxy(s);
    if (proxied) return proxied;

    try {
      const storage = getStorage();
      const storageRef = ref(storage, s);
      const bytes = await getBytes(storageRef);
      const mime = guessImageMimeFromUrl(s);
      return await bytesToDataUrl(bytes, mime);
    } catch (e) {
      console.warn("[pdf] Firebase Storage getBytes failed, trying fetch:", e);
    }
  }

  try {
    const res = await fetch(s, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await blobToDataUrl(blob);
  } catch (e) {
    console.warn("[pdf] Could not inline image for PDF:", e);
    return null;
  }
}

function waitForImageLoad(img) {
  if (!img) return Promise.resolve();
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Image failed to load"));
  });
}

// Helper function to render custom SVG icons
const renderRoleIcon = (svgCode, roleName) => {
  if (!svgCode || !svgCode.trim()) return null;
  
  // Basic validation to ensure it's SVG content
  if (!svgCode.includes('<svg')) return null;
  
  // Make Facilitator and High Motor icons slightly bigger
  const isLargerIcon = roleName === 'Facilitator' || roleName === 'High Motor';
  const iconSize = isLargerIcon ? 70 : 60;
  const viewBoxSize = isLargerIcon ? 70 : 60;
  
  // Process SVG to optimize for display with white color
  const processedSvg = svgCode.replace(
    /<svg([^>]*)>/g,
    `<svg$1 width="${iconSize}" height="${iconSize}" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}" style="display: block; width: 100%; height: 100%; margin: 0; padding: 0; fill: white; color: white;">`
  );
  
  return (
    <div 
      style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        verticalAlign: 'middle',
        overflow: 'hidden',
        flexShrink: 0,
        margin: 0,
        padding: 0
      }}
      dangerouslySetInnerHTML={{ __html: processedSvg }}
    />
  );
};

/** Keep each PDF row short enough that html2pdf is less likely to slice mid-block across pages. */
const PRINT_NUGGET_MAX_CHARS = 480;

function splitLongNuggetForPdf(text) {
  const t = String(text ?? "").trim();
  if (!t) return [];
  if (t.length <= PRINT_NUGGET_MAX_CHARS) return [t];
  const parts = [];
  let rest = t;
  while (rest.length > PRINT_NUGGET_MAX_CHARS) {
    let cut = rest.lastIndexOf(" ", PRINT_NUGGET_MAX_CHARS);
    if (cut < 220) cut = PRINT_NUGGET_MAX_CHARS;
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}

const GameLogStatsTable = ({ stats }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc',
    compositeState: {}
  });
  const [typeFilter, setTypeFilter] = useState('ALL');

  const typeFilterOptions = ['ALL', 'HS', 'AAU', 'CAMP', 'INTL', 'OTHER'];

  // Helper function to check if a stat entry has no statistical data (player didn't play)
  const hasNoStats = (stat) => {
    // Check if minutes exist and have a meaningful value
    const minValue = stat.min;
    const hasMinutes = minValue && 
      minValue !== '' && 
      minValue !== null && 
      minValue !== undefined &&
      String(minValue).trim() !== '' &&
      String(minValue).trim() !== '0:00' &&
      String(minValue).trim() !== '0:00:00';
    
    // If player has meaningful minutes, they played - don't filter out
    if (hasMinutes) return false;
    
    // Check all other stat fields - if any has a non-zero value, player played
    const statFields = [
      'fgFga', 'fgPct', 'threePThreePA', 'threeFgPct', 
      'ftFta', 'ftPct', 'orb', 'drb', 'trb', 'ast', 'blk', 'stl', 'to', 'pf', 'pts'
    ];
    
    // If any stat field has a non-zero value, player played
    const hasAnyStat = statFields.some(field => {
      const value = stat[field];
      // Check if value is empty, null, undefined, 0, or empty string
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') {
        // For composite fields like "1-2", check if it has non-zero values
        if (value.includes('-')) {
          const parts = value.split('-').map(Number);
          return parts[0] > 0 || parts[1] > 0;
        }
        // For regular strings, check if it's not empty
        return value.trim() !== '';
      }
      // For numbers, check if it's non-zero
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    });
    
    // If no minutes and no stats, player didn't play - filter out
    return !hasAnyStat;
  };

  const columns = [
    { key: 'date', title: "Date", type: 'date' },
    { key: 'season', title: "Season", type: 'string' },
    { key: 'type', title: "Type", type: 'string', isFilter: true },
    { key: 'event', title: "Event", type: 'string' },
    { key: 'team', title: "Team", type: 'string' },
    { key: 'opponent', title: "Opponent", type: 'string' },
    { key: 'result', title: "Result", type: 'string' },
    { 
      key: 'min', 
      title: "MIN", 
      type: 'number',
      render: (value) => {
        if (!value) return '--';
        const str = String(value);
        // Remove trailing ":00" if present (e.g., "35:15:00" -> "35:15")
        return str.replace(/:00$/, '');
      }
    },
    { 
      key: 'fgFga', 
      title: "FG-FGA",
      composite: true,
      type: 'composite'
    },
    { 
      key: 'fgPct', 
      title: "FG%",
      type: 'number',
      render: (value) => {
        const num = parseFloat(value);
        return !isNaN(num) ? num.toFixed(1) : '--';
      }
    },
    { 
      key: 'threePThreePA', 
      title: "3P-3PA",
      composite: true,
      type: 'composite'
    },
    { 
      key: 'threeFgPct', 
      title: "3FG%",
      type: 'number',
      render: (value) => {
        const num = parseFloat(value);
        return !isNaN(num) ? num.toFixed(1) : '--';
      }
    },
    { 
      key: 'ftFta', 
      title: "FT-FTA",
      composite: true,
      type: 'composite'
    },
    { 
      key: 'ftPct', 
      title: "FT%",
      type: 'number',
      render: (value) => {
        const num = parseFloat(value);
        return !isNaN(num) ? num.toFixed(1) : '--';
      }
    },
    { key: 'orb', title: "ORB", type: 'number' },
    { key: 'drb', title: "DRB", type: 'number' },
    { key: 'trb', title: "TRB", type: 'number' },
    { key: 'ast', title: "AST", type: 'number' },
    { key: 'blk', title: "BLK", type: 'number' },
    { key: 'stl', title: "STL", type: 'number' },
    { key: 'to', title: "TO", type: 'number' },
    { key: 'pf', title: "PF", type: 'number' },
    { key: 'pts', title: "PTS", type: 'number' }
  ];

  const handleSort = (column) => {
    setSortConfig(prev => {
      const isComposite = column.composite;
      const currentState = prev.compositeState[column.key] || 0;
      
      if (prev.key !== column.key) {
        return {
          key: column.key,
          direction: isComposite ? 'asc' : 'asc',
          compositeState: {
            ...prev.compositeState,
            [column.key]: isComposite ? 1 : 1
          }
        };
      }

      if (isComposite) {
        const nextState = (currentState + 1) % 5;
        if (nextState === 0) {
          return {
            key: 'date',
            direction: 'desc',
            compositeState: {
              ...prev.compositeState,
              [column.key]: 0
            }
          };
        }
        
        return {
          key: column.key,
          direction: nextState % 2 === 1 ? 'asc' : 'desc',
          compositeState: {
            ...prev.compositeState,
            [column.key]: nextState
          }
        };
      } else {
        const nextState = (currentState + 1) % 3;
        if (nextState === 0) {
          return {
            key: 'date',
            direction: 'desc',
            compositeState: {
              ...prev.compositeState,
              [column.key]: 0
            }
          };
        }
        
        return {
          key: column.key,
          direction: nextState === 1 ? 'asc' : 'desc',
          compositeState: {
            ...prev.compositeState,
            [column.key]: nextState
          }
        };
      }
    });
  };

  // Apply filtering and sorting
  const filteredAndSortedStats = useMemo(() => {
    try {
      // First filter out stats with no data (player didn't play)
      const statsWithData = stats.filter(stat => !hasNoStats(stat));
      
      // Then apply type filter
      const filteredStats = typeFilter === 'ALL' 
        ? [...statsWithData] 
        : statsWithData.filter(stat => stat.type === typeFilter);

      // Then apply sorting
      const { key, direction, compositeState } = sortConfig;
      const column = columns.find(col => col.key === key);
      const currentCompositeState = compositeState[key] || 0;

      return filteredStats.sort((a, b) => {
        // Handle composite fields (FG-FGA, 3P-3PA, FT-FTA)
        if (column?.composite) {
          // Parse the string values ("1-2") into numbers
          const parseComposite = (value) => {
            if (!value) return [0, 0];
            const parts = String(value).split('-').map(Number);
            return [parts[0] || 0, parts[1] || 0];
          };
          
          const [aFirst, aSecond] = parseComposite(a[key]);
          const [bFirst, bSecond] = parseComposite(b[key]);
          
          switch (currentCompositeState) {
            case 1: return aFirst - bFirst;
            case 2: return bFirst - aFirst;
            case 3: return aSecond - bSecond;
            case 4: return bSecond - aSecond;
            default: return 0;
          }
        }
        
        // Handle regular fields
        let aVal = a[key];
        let bVal = b[key];
        
        // Handle different data types
        if (column?.type === 'date') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        } 
        else if (column?.type === 'string') {
          aVal = String(aVal || '');
          bVal = String(bVal || '');
          return direction === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
        } 
        else { // numbers
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
      });
    } catch (error) {
      console.error("Sorting error:", error);
      return stats;
    }
  }, [stats, sortConfig, typeFilter]);

  // Calculate totals and averages for filtered stats
  const calculateTotalsAndAverages = useMemo(() => {
    // First filter out stats with no data (player didn't play)
    const statsWithData = stats.filter(stat => !hasNoStats(stat));
    
    // Then apply type filter
    const filteredStats = typeFilter === 'ALL' 
      ? [...statsWithData] 
      : statsWithData.filter(stat => stat.type === typeFilter);

    if (filteredStats.length === 0) {
      return { totals: {}, averages: {}, gamesPlayed: 0, record: { wins: 0, losses: 0 } };
    }

    const totals = {
      min: 0,
      fgMade: 0,
      fgAttempted: 0,
      threePMade: 0,
      threePAttempted: 0,
      ftMade: 0,
      ftAttempted: 0,
      orb: 0,
      drb: 0,
      trb: 0,
      ast: 0,
      blk: 0,
      stl: 0,
      to: 0,
      pf: 0,
      pts: 0
    };

    let wins = 0;
    let losses = 0;

    filteredStats.forEach(stat => {
      // Parse minutes (format: "MM:SS" or "MM:SS:00")
      const minParts = (stat.min || '0:00').split(':');
      const minutes = parseInt(minParts[0]) + (parseInt(minParts[1]) / 60);
      totals.min += minutes;

      // Parse composite fields
      const parseComposite = (value) => {
        if (!value) return [0, 0];
        const parts = String(value).split('-').map(Number);
        return [parts[0] || 0, parts[1] || 0];
      };

      const [fgMade, fgAttempted] = parseComposite(stat.fgFga);
      const [threePMade, threePAttempted] = parseComposite(stat.threePThreePA);
      const [ftMade, ftAttempted] = parseComposite(stat.ftFta);

      totals.fgMade += fgMade;
      totals.fgAttempted += fgAttempted;
      totals.threePMade += threePMade;
      totals.threePAttempted += threePAttempted;
      totals.ftMade += ftMade;
      totals.ftAttempted += ftAttempted;

      // Add other stats
      totals.orb += parseFloat(stat.orb) || 0;
      totals.drb += parseFloat(stat.drb) || 0;
      totals.trb += parseFloat(stat.trb) || 0;
      totals.ast += parseFloat(stat.ast) || 0;
      totals.blk += parseFloat(stat.blk) || 0;
      totals.stl += parseFloat(stat.stl) || 0;
      totals.to += parseFloat(stat.to) || 0;
      totals.pf += parseFloat(stat.pf) || 0;
      totals.pts += parseFloat(stat.pts) || 0;

      // Count wins/losses
      const result = stat.result || '';
      if (result.startsWith('W')) wins++;
      else if (result.startsWith('L')) losses++;
    });

    const gamesPlayed = filteredStats.length;
    const averages = {
      min: gamesPlayed > 0 ? (totals.min / gamesPlayed).toFixed(1) : 0,
      fgMade: gamesPlayed > 0 ? (totals.fgMade / gamesPlayed).toFixed(1) : 0,
      fgAttempted: gamesPlayed > 0 ? (totals.fgAttempted / gamesPlayed).toFixed(1) : 0,
      fgPct: totals.fgAttempted > 0 ? ((totals.fgMade / totals.fgAttempted) * 100).toFixed(1) : 0,
      threePMade: gamesPlayed > 0 ? (totals.threePMade / gamesPlayed).toFixed(1) : 0,
      threePAttempted: gamesPlayed > 0 ? (totals.threePAttempted / gamesPlayed).toFixed(1) : 0,
      threeFgPct: totals.threePAttempted > 0 ? ((totals.threePMade / totals.threePAttempted) * 100).toFixed(1) : 0,
      ftMade: gamesPlayed > 0 ? (totals.ftMade / gamesPlayed).toFixed(1) : 0,
      ftAttempted: gamesPlayed > 0 ? (totals.ftAttempted / gamesPlayed).toFixed(1) : 0,
      ftPct: totals.ftAttempted > 0 ? ((totals.ftMade / totals.ftAttempted) * 100).toFixed(1) : 0,
      orb: gamesPlayed > 0 ? (totals.orb / gamesPlayed).toFixed(1) : 0,
      drb: gamesPlayed > 0 ? (totals.drb / gamesPlayed).toFixed(1) : 0,
      trb: gamesPlayed > 0 ? (totals.trb / gamesPlayed).toFixed(1) : 0,
      ast: gamesPlayed > 0 ? (totals.ast / gamesPlayed).toFixed(1) : 0,
      blk: gamesPlayed > 0 ? (totals.blk / gamesPlayed).toFixed(1) : 0,
      stl: gamesPlayed > 0 ? (totals.stl / gamesPlayed).toFixed(1) : 0,
      to: gamesPlayed > 0 ? (totals.to / gamesPlayed).toFixed(1) : 0,
      pf: gamesPlayed > 0 ? (totals.pf / gamesPlayed).toFixed(1) : 0,
      pts: gamesPlayed > 0 ? (totals.pts / gamesPlayed).toFixed(1) : 0
    };

    return {
      totals: {
        ...totals,
        fgPct: totals.fgAttempted > 0 ? ((totals.fgMade / totals.fgAttempted) * 100).toFixed(1) : 0,
        threeFgPct: totals.threePAttempted > 0 ? ((totals.threePMade / totals.threePAttempted) * 100).toFixed(1) : 0,
        ftPct: totals.ftAttempted > 0 ? ((totals.ftMade / totals.ftAttempted) * 100).toFixed(1) : 0
      },
      averages,
      gamesPlayed,
      record: { wins, losses }
    };
  }, [stats, typeFilter]);

  // Get sort indicator symbol
  const getSortSymbol = (columnKey) => {
    if (sortConfig.key !== columnKey) return '';
    
    if (columnKey === 'date' && sortConfig.direction === 'desc') {
      return ' ↓';
    }
    
    const currentState = sortConfig.compositeState[columnKey] || 0;
    
    if (currentState === 0) return '';
    if (currentState % 2 === 1) return ' ↑';
    return ' ↓';
  };

  return (
    <div className={styles.statsTableWrapper}>
      <table className={styles.statsTable}>
        <thead>
          <tr>
            {columns.map(column => (
              <th 
                key={column.key}
                onClick={() => column.isFilter ? null : handleSort(column)}
                style={{ cursor: column.isFilter ? 'default' : 'pointer' }}
              >
                {column.isFilter ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{column.title}</span>
                    <select 
                      value={typeFilter} 
                      onChange={(e) => setTypeFilter(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        padding: '2px 4px', 
                        fontSize: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        backgroundColor: 'white'
                      }}
                    >
                      {typeFilterOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                {column.title}
                {getSortSymbol(column.key)}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedStats.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {columns.map(column => {
                if (column.key === "result") {
                  const result = row.result || '';
                  const resultLetter = result.charAt(0) || "-";
                  const rest = result.substring(1).trim() || "";
                  const isWin = resultLetter === "W";
                  
                  return (
                    <td key={`${rowIndex}-${column.key}`}>
                      <span style={{ color: isWin ? "green" : "red", fontWeight: 600 }}>
                        {resultLetter}
                      </span>{" "}
                      {rest}
                    </td>
                  );
                }
                
                const value = row[column.key];
                
                if (column.render) {
                  return (
                    <td key={`${rowIndex}-${column.key}`}>
                      {column.render(value)}
                    </td>
                  );
                }
                
                // For composite fields, just display the string as-is
                if (column.composite) {
                  return (
                    <td key={`${rowIndex}-${column.key}`}>
                      {value || "--"}
                    </td>
                  );
                }
                
                return (
                  <td key={`${rowIndex}-${column.key}`}>
                    {value || "--"}
                  </td>
                );
              })}
            </tr>
          ))}
          
          {/* Totals Row */}
          {calculateTotalsAndAverages.gamesPlayed > 0 && (
            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
              <td><strong>Totals</strong></td>
              <td><strong>{typeFilter === 'ALL' ? 'Overall' : typeFilter}</strong></td>
              <td><strong>GP: {calculateTotalsAndAverages.gamesPlayed}</strong></td>
              <td></td>
              <td></td>
              <td></td>
              <td><strong>{calculateTotalsAndAverages.record.wins} W - {calculateTotalsAndAverages.record.losses} L</strong></td>
              <td><strong>{Math.floor(calculateTotalsAndAverages.totals.min)}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.fgMade}-{calculateTotalsAndAverages.totals.fgAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.fgPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.threePMade}-{calculateTotalsAndAverages.totals.threePAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.threeFgPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.ftMade}-{calculateTotalsAndAverages.totals.ftAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.ftPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.orb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.drb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.trb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.ast}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.blk}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.stl}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.to}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.pf}</strong></td>
              <td><strong>{calculateTotalsAndAverages.totals.pts}</strong></td>
            </tr>
          )}
          
          {/* Averages Row */}
          {calculateTotalsAndAverages.gamesPlayed > 0 && (
            <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
              <td><strong>Averages</strong></td>
              <td><strong>{typeFilter === 'ALL' ? 'Overall' : typeFilter}</strong></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td><strong>{calculateTotalsAndAverages.averages.min}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.fgMade}-{calculateTotalsAndAverages.averages.fgAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.fgPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.threePMade}-{calculateTotalsAndAverages.averages.threePAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.threeFgPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.ftMade}-{calculateTotalsAndAverages.averages.ftAttempted}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.ftPct}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.orb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.drb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.trb}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.ast}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.blk}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.stl}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.to}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.pf}</strong></td>
              <td><strong>{calculateTotalsAndAverages.averages.pts}</strong></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const PreviewDetails = ({
  playerProfileData,
  bgOverviewData,
  scoutingOverviewData,
  id,
  compare,
}) => {
  const { isAdmin, isSuperAdmin, userInfo } = useAuth();
  // Executive and NCAA subscribers can view these read-only profile sections.
  const canViewMeasurementsAndKeyContacts =
    isAdmin() || isSuperAdmin() || ["executive", "ncaa"].includes(userInfo?.memberType);
  const history = useHistory();

  const [compareModal, setCompareModal] = useState(false);
  const [isWatchListed, setIsWatchListed] = useState(false);
  const [reportsData, setReports] = useState([]);
  const [gameLogStats, setGameLogStats] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [rolesData, setRolesData] = useState([]);
  const [roleCounts, setRoleCounts] = useState({});
  const [reportCounts, setReportCounts] = useState({}); // Count of unique reports per role
  const [roleExplanations, setRoleExplanations] = useState({});
  const [roleAdjustments, setRoleAdjustments] = useState({});
  const [roleIcons, setRoleIcons] = useState({});
  const [measurements, setMeasurements] = useState([]);
  const [keyContacts, setKeyContacts] = useState([]);
  const [accordionActiveKeys, setAccordionActiveKeys] = useState([]);
  const [isPrintingDigest, setIsPrintingDigest] = useState(false);
  const [activeVideoPlayers, setActiveVideoPlayers] = useState({});
  const [videoThumbVariant, setVideoThumbVariant] = useState({});
  const printDigestRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // More comprehensive mobile detection
  const isMobile = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
    const isSmallScreen = windowWidth <= 1024;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    return isMobileDevice || (isSmallScreen && isTouchDevice);
  };

  const isMobileView = isMobile();

  const formatCompositeRankDisplay = (value) => {
    if (value === null || value === undefined) return value;
    const s = String(value).trim();
    if (!s) return s;
    if (/^#\d+$/.test(s)) return s;
    const ordinal = s.match(/^(\d{1,4})(st|nd|rd|th)$/i);
    if (ordinal) return `#${ordinal[1]}`;
    if (/^\d{1,4}$/.test(s)) return `#${s}`;
    return s;
  };

  const compositeRankingItems = useMemo(() => {
    const rankings = playerProfileData?.compositeRankings || {};
    // Temporarily hide On3/Rivals until Cloudflare blocking is resolved for the scraper.
    const items = [
      {
        key: "rsci",
        label: "RSCI",
        value: rankings.rsci,
        url: playerProfileData?.rivalsRankingUrl || "",
      },
      {
        key: "ranking247",
        label: "247Sports",
        value: rankings.ranking247,
        url: playerProfileData?.ranking247Url || "",
      },
      {
        key: "espn",
        label: "ESPN",
        value: rankings.espn,
        url: playerProfileData?.espnRankingUrl || "",
      },
    ];

    return items.filter((item) => isDisplayableIndustryRankValue(item.value));
  }, [playerProfileData]);

  const hasCompositeRankings = compositeRankingItems.length > 0;

  /**
   * Print digest headshot URL. PDF generation inlines bytes via the proxyStorageImageForPdf
   * Cloud Function (or bucket CORS + fetch); see resolveImageUrlToDataUrlForPdf.
   */
  const printDigestPhotoSrc = useMemo(() => {
    const raw = playerProfileData?.Headshot?.trim();
    if (!raw || raw === "null") return defaultImage;
    if (raw.startsWith("data:")) return raw;
    if (typeof window === "undefined") return raw;
    try {
      return new URL(raw, window.location.href).href;
    } catch {
      return defaultImage;
    }
  }, [playerProfileData?.Headshot]);

  const printDigestHeadshotCrossOrigin = useMemo(() => {
    if (!printDigestPhotoSrc || printDigestPhotoSrc.startsWith("data:")) return undefined;
    try {
      const u = new URL(printDigestPhotoSrc, window.location.href);
      if (u.origin === window.location.origin) return undefined;
      return "anonymous";
    } catch {
      return undefined;
    }
  }, [printDigestPhotoSrc]);

  const formatStatValue = (val) => {
    if (val === null || val === undefined) return "-";
    const s = String(val).trim();
    return s === "" ? "-" : s;
  };

  const projectionRangeText =
    playerProfileData?.pRange &&
    playerProfileData?.Prlow !== "" &&
    playerProfileData?.Prhigh !== ""
      ? playerProfileData.Prlow === playerProfileData.Prhigh
        ? playerProfileData.Prhigh
        : `${playerProfileData.Prlow} to ${playerProfileData.Prhigh}`
      : "-";

  const dobText = playerProfileData?.DOB
    ? moment(playerProfileData.DOB).format("MM/DD/YY")
    : "--";

  const ageText = formatProfileAge(playerProfileData?.DOB, playerProfileData?.Age);

  const piScoreText =
    (isAdmin() || isSuperAdmin()) && playerProfileData?.overallScore
      ? playerProfileData.overallScore
      : "Coming Soon";

  const aauTeamText =
    playerProfileData?.aauTeam != null &&
    String(playerProfileData.aauTeam).trim() !== ""
      ? String(playerProfileData.aauTeam).trim()
      : "--";

  const recruitmentStatusText = playerProfileData?.committedTeam
    ? `Committed: ${playerProfileData.committedTeam}`
    : playerProfileData?.Status || "";

  const hometownDisplayText = playerProfileData.hometown_City
    ? `${playerProfileData.hometown_City}${
        playerProfileData.hometown_State_Country
          ? `, ${playerProfileData.hometown_State_Country}`
          : ""
      }`
    : playerProfileData.hometown_State_Country || "-";

  const currentTeamDesktopText =
    playerProfileData?.Team &&
    !String(playerProfileData.Team).toLowerCase().startsWith("committed")
      ? playerProfileData.Team
      : "-";

  const currentTeamMobileText = (() => {
    const team = playerProfileData?.Team;
    if (!team) return "-";
    if (team === "Transfer Portal" && !(isAdmin() || isSuperAdmin())) return "-";
    return team;
  })();

  const leftHeaderColProps = compare ? { xs: 12 } : { md: 7 };
  const rightHeaderColProps = compare ? { xs: 12 } : { md: 5 };

  const escapeHtml = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const renderBullets = (items) => {
    if (!Array.isArray(items) || items.length === 0) return `<div class="muted">-</div>`;
    const lis = items
      .map((it) => {
        const txt = typeof it === "string" ? it : it && typeof it === "object" ? it.note || it.value : String(it || "");
        const clean = String(txt).trim();
        if (!clean) return null;
        return `<li>${escapeHtml(clean)}</li>`;
      })
      .filter(Boolean)
      .join("");
    return `<ul>${lis}</ul>`;
  };

  const handlePrintDigest = async () => {
    setIsPrintingDigest(true);
    let headshotImgEl = null;
    let headshotRestoreSrc = null;
    let headshotRestoreCrossorigin = null;
    try {
      // Allow the UI to render the spinner/disabled state before heavy work starts.
      await new Promise((r) => setTimeout(r, 50));

      // Keep digest mounted offscreen so printing doesn't mount/unmount a large subtree.
      await new Promise((r) => setTimeout(r, 50));

      const node = printDigestRef.current;
      if (!node) throw new Error("Print digest node not available");

      // Inline headshot as data URL via Firebase SDK (or fetch) so html2canvas is not CORS-tainted.
      headshotImgEl = node.querySelector("img[data-print-headshot]");
      if (headshotImgEl && printDigestPhotoSrc && !String(printDigestPhotoSrc).startsWith("data:")) {
        const inlined = await resolveImageUrlToDataUrlForPdf(printDigestPhotoSrc);
        if (inlined) {
          headshotRestoreSrc = headshotImgEl.getAttribute("src");
          headshotRestoreCrossorigin = headshotImgEl.getAttribute("crossorigin");
          headshotImgEl.removeAttribute("crossorigin");
          headshotImgEl.setAttribute("src", inlined);
          await waitForImageLoad(headshotImgEl);
        }
      }

      const sanitizePdfSegment = (s) =>
        String(s || "")
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_-]/g, "");

      const firstSeg = sanitizePdfSegment(playerProfileData.FirstName);
      const lastSeg = sanitizePdfSegment(playerProfileData.LastName);
      const pdfFileName =
        firstSeg && lastSeg
          ? `${firstSeg}_${lastSeg}.pdf`
          : firstSeg
            ? `${firstSeg}.pdf`
            : lastSeg
              ? `${lastSeg}.pdf`
              : "player.pdf";

      const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      const sameOriginLogoSrc = `${window.location.origin}${publicUrl}/logo512.png`;

      const pdfRenderScale = Math.max(3, Math.min(4, (window.devicePixelRatio || 1) * 2));

      const buildPdfFromNode = async (node) => {
        await html2pdf()
          .set({
            margin: [14, 14, 14, 14],
            filename: pdfFileName,
            // Keep text/lines crisp in the rasterized HTML capture.
            image: { type: "png", quality: 1 },
            html2canvas: {
              // scale: 1 produces a low-DPI capture that looks blurry in A4 PDFs.
              scale: pdfRenderScale,
              // Render against the full digest dimensions (not viewport dimensions).
              windowWidth: node.scrollWidth,
              windowHeight: node.scrollHeight,
              // We rely on proper CORS headers to include external images.
              // If not present, we'll retry with same-origin logo swap (below).
              useCORS: true,
              allowTaint: false,
              backgroundColor: "#ffffff",
              scrollY: 0,
              scrollX: 0,
              logging: false,
              imageTimeout: 15000,
            },
            pagebreak: {
              mode: ["css", "legacy"],
              avoid: [".print-bullet-row", ".print-section-title", ".print-section-lead"],
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .from(node)
          .save();
      };

      const swapExternalImagesForLogo = (root) => {
        Array.from(root.querySelectorAll("img")).forEach((img) => {
          const src = img.getAttribute("src") || "";
          if (!src || src.startsWith("data:")) return;
          try {
            const u = new URL(src, window.location.href);
            if (u.origin !== window.location.origin) {
              img.setAttribute("src", sameOriginLogoSrc);
              img.removeAttribute("crossorigin");
            }
          } catch {
            img.setAttribute("src", sameOriginLogoSrc);
            img.removeAttribute("crossorigin");
          }
        });
      };

      // Attempt 1: include everything. Data URL images must not use crossorigin (not needed).
      try {
        Array.from(node.querySelectorAll("img")).forEach((img) => {
          const src = img.getAttribute("src") || "";
          if (src.startsWith("data:")) {
            img.removeAttribute("crossorigin");
            return;
          }
          try {
            img.setAttribute("crossorigin", "anonymous");
          } catch (e) {}
        });
        await buildPdfFromNode(node);
      } catch (e) {
        const msg = (e && e.message) ? String(e.message) : String(e || "");
        const isTaintError =
          msg.toLowerCase().includes("tainted canvases may not be exported") ||
          msg.toLowerCase().includes("todataurl");

        if (!isTaintError) throw e;

        // Attempt 2: same-origin logo for any cross-origin image (headshots, etc.)
        const clone = node.cloneNode(true);
        swapExternalImagesForLogo(clone);
        await buildPdfFromNode(clone);
      }
    } catch (err) {
      console.error("Print digest error:", err);
      alert("Could not generate print PDF. Please try again.");
    } finally {
      if (headshotImgEl && headshotRestoreSrc != null) {
        headshotImgEl.setAttribute("src", headshotRestoreSrc);
        if (headshotRestoreCrossorigin) {
          headshotImgEl.setAttribute("crossorigin", headshotRestoreCrossorigin);
        } else {
          headshotImgEl.removeAttribute("crossorigin");
        }
      }
      setIsPrintingDigest(false);
    }
  };

  useEffect(() => {
    const fetchGameLogs = async () => {
      try {
        const snapshot = await db
          .collection("game-log-stats")
          .where("playerProfileId", "==", id)
          .get();
        const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setGameLogStats(logs);
      } catch (error) {
        console.error("Failed to fetch game logs:", error);
      }
    };
    fetchGameLogs();
  }, [id]);

  useEffect(() => {
    if (!canViewMeasurementsAndKeyContacts) {
      setMeasurements([]);
      return;
    }
    const fetchMeasurements = async () => {
      if (!id) return;
      try {
        const snapshot = await db
          .collection("player-measurements")
          .where("playerProfile", "==", id)
          .get();
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => {
          const da = a.date ? new Date(a.date) : 0;
          const dbDate = b.date ? new Date(b.date) : 0;
          return dbDate - da;
        });
        setMeasurements(data);
      } catch (error) {
        console.error("Failed to fetch measurements:", error);
      }
    };
    fetchMeasurements();
  }, [id, canViewMeasurementsAndKeyContacts]);

  useEffect(() => {
    if (!canViewMeasurementsAndKeyContacts) {
      setKeyContacts([]);
      return;
    }
    const fetchKeyContacts = async () => {
      if (!id) return;
      try {
        const snapshot = await db
          .collection("player-key-contacts")
          .where("playerProfile", "==", id)
          .get();
        const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        rows.sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setKeyContacts(rows);
      } catch (error) {
        console.error("Failed to fetch key contacts:", error);
      }
    };
    fetchKeyContacts();
  }, [id, canViewMeasurementsAndKeyContacts]);

  const handleAccordionSelect = (eventKey) => {
    setAccordionActiveKeys((prev) =>
      prev.includes(eventKey) ? prev.filter((k) => k !== eventKey) : [...prev, eventKey]
    );
  };

  const handleVideoThumbError = (index) => {
    setVideoThumbVariant((prev) => ({ ...prev, [index]: "hqdefault" }));
  };

  const handleEnableVideoPlayer = (index) => {
    setActiveVideoPlayers((prev) => ({ ...prev, [index]: true }));
  };

  const isPlaceholderFamilyMember = (member) => {
    const m = String(member ?? "").trim();
    return m === "" || m === "---" || m === "--" || m === "--:";
  };

  const splitPrintLines = (text) =>
    String(text || "")
      .split(/\r?\n+/)
      .map((x) => x.trim())
      .filter(Boolean);

  const linesFromFamilyRow = (item) => {
    const m = String(item?.member ?? "").trim();
    const n = String(item?.note ?? "").trim();
    const noteLines = n ? splitPrintLines(n) : [];
    if (isPlaceholderFamilyMember(m)) {
      return noteLines;
    }
    if (noteLines.length === 0) {
      return m ? [m] : [];
    }
    return noteLines.map((line) => `${m}: ${line}`);
  };

  const expandListForPrintBullets = (arr) =>
    Array.isArray(arr)
      ? arr
          .flatMap((it) => {
            if (
              it &&
              typeof it === "object" &&
              !Array.isArray(it) &&
              Object.prototype.hasOwnProperty.call(it, "member")
            ) {
              return linesFromFamilyRow(it);
            }
            const raw =
              typeof it === "string"
                ? it
                : it && typeof it === "object"
                  ? String(it.note ?? it.value ?? "").trim()
                  : String(it ?? "").trim();
            return splitPrintLines(raw);
          })
          .filter(Boolean)
      : [];

  const recruitmentStringsForPrint = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((it) =>
        typeof it === "string" ? it.trim() : String(it?.note || it?.value || "").trim()
      )
      .filter(Boolean);

  const printCommaListFromStrings = (strings) => {
    if (!Array.isArray(strings) || strings.length === 0) return null;
    return <div className={styles.printComma}>{strings.join(", ")}</div>;
  };

  const printRecruitmentNotesPlain = (text) => {
    const t = String(text ?? "");
    if (!t.trim()) return null;
    return <div className={styles.printNotesPlain}>{t}</div>;
  };

  const visitsPrint = recruitmentStringsForPrint(bgOverviewData?.recruitment?.notableVisits);
  const offersPrint = recruitmentStringsForPrint(bgOverviewData?.recruitment?.notableOffers);
  const interestsPrint = recruitmentStringsForPrint(bgOverviewData?.recruitment?.notableInterests);
  const commitmentPrint = String(bgOverviewData?.recruitment?.commitment || "").trim();
  const hasRecruitmentNotesPrint = Boolean(String(bgOverviewData?.recruitment?.notes || "").trim());
  const showRecruitmentPrintSection =
    !!bgOverviewData &&
    (Boolean(commitmentPrint) ||
      visitsPrint.length > 0 ||
      offersPrint.length > 0 ||
      interestsPrint.length > 0 ||
      hasRecruitmentNotesPrint);

  const familyCirclePrintLines = Array.isArray(bgOverviewData?.personal?.familyCircleMembers)
    ? bgOverviewData.personal.familyCircleMembers.flatMap((row) => linesFromFamilyRow(row))
    : [];
  const hasFamilyCirclePrint = familyCirclePrintLines.length > 0;

  const nuggetsPrintLines = expandListForPrintBullets(bgOverviewData?.personal?.nuggets);
  const hasNuggetsPrint = nuggetsPrintLines.length > 0;
  const nuggetsPrintRows = nuggetsPrintLines.flatMap((line, lineIdx) => {
    const chunks = splitLongNuggetForPdf(line);
    return chunks.map((chunk, chunkIdx) => ({
      key: `ng-${lineIdx}-${chunkIdx}-${chunk.slice(0, 40)}`,
      text: chunk,
      showBullet: chunkIdx === 0,
    }));
  });

  const bgSocial =
    bgOverviewData?.socialMedia || bgOverviewData?.SocialMedia || {};

  const pickSocialHandle = (...candidates) => {
    for (const c of candidates) {
      let s = String(c ?? "").trim();
      if (!s) continue;
      s = s.replace(/^@+/, "").trim();
      if (s) return s;
    }
    return "";
  };

  const trimAlt = (a, b) => String(a ?? b ?? "").trim();

  const twitterNuggetsPrintLines = expandListForPrintBullets(bgSocial?.twitter?.nuggets);
  const instagramNuggetsPrintLines = expandListForPrintBullets(bgSocial?.instagram?.nuggets);

  const twitterHandlePrint = pickSocialHandle(
    playerProfileData?.Twitter,
    playerProfileData?.twitter,
    playerProfileData?.TwitterHandle,
    playerProfileData?.twitterHandle
  );
  const instagramHandlePrint = pickSocialHandle(
    playerProfileData?.Instagram,
    playerProfileData?.instagram,
    playerProfileData?.InstagramHandle,
    playerProfileData?.instagramHandle
  );
  const twitterAccountBg = String(bgSocial?.twitter?.account || "").trim();
  const instagramAccountBg = String(bgSocial?.instagram?.account || "").trim();
  const twitterDmsRaw = bgSocial?.twitter?.dms;
  const twitterDmsPrint =
    twitterDmsRaw !== undefined && twitterDmsRaw !== null
      ? String(twitterDmsRaw).trim()
      : "";

  const hasTwitterPrintBlock =
    Boolean(twitterHandlePrint) ||
    twitterNuggetsPrintLines.length > 0 ||
    Boolean(twitterAccountBg) ||
    Boolean(twitterDmsPrint);

  const hasInstagramPrintBlock =
    Boolean(instagramHandlePrint) ||
    instagramNuggetsPrintLines.length > 0 ||
    Boolean(instagramAccountBg);

  const snapchatPrint = trimAlt(playerProfileData?.Snapchat, playerProfileData?.snapchat);
  const tiktokPrint = trimAlt(playerProfileData?.TikTok, playerProfileData?.tiktok);

  const hasSocialsPrintSection =
    hasTwitterPrintBlock ||
    hasInstagramPrintBlock ||
    Boolean(snapchatPrint) ||
    Boolean(tiktokPrint);

  const hasSocialMediaSection =
    Boolean(twitterHandlePrint) ||
    Boolean(instagramHandlePrint) ||
    Boolean(snapchatPrint) ||
    Boolean(tiktokPrint);

  const formatMeasurementDate = (val) => {
    if (!val) return "--";
    if (val.includes && val.includes("/")) return val;
    const d = typeof val === "string" ? new Date(val) : val;
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  // Function to fetch role explanations and icons from roles-dropdown collection
  const fetchRoleExplanations = async (roleNames) => {
    try {
      const explanations = {};
      const icons = {};
      
      for (const roleName of roleNames) {
        try {
          const response = await db
            .collection("roles-dropdown")
            .where("name", "==", roleName)
            .get();
          
          if (!response.empty) {
            const roleDoc = response.docs[0];
            const roleData = roleDoc.data();
            explanations[roleName] = roleData.explanation || "";
            icons[roleName] = roleData.icon || "";
          } else {
            explanations[roleName] = "";
            icons[roleName] = "";
          }
        } catch (error) {
          console.log(`Error fetching explanation for ${roleName}:`, error);
          explanations[roleName] = "";
          icons[roleName] = "";
        }
      }
      
      setRoleExplanations(explanations);
      setRoleIcons(icons);
    } catch (error) {
      console.log("Error fetching role explanations:", error);
    }
  };

  // Function to fetch role adjustments for this player
  const fetchRoleAdjustments = async () => {
    try {
      console.log('Fetching role adjustments for playerID:', id);
      const response = await db
        .collection("role-adjustments")
        .where("playerID", "==", id)
        .get();
      
      console.log('Role adjustments response:', response.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const adjustments = {};
      response.docs.forEach(doc => {
        const data = doc.data();
        adjustments[data.role] = data.count || 0;
      });
      
      console.log('Processed role adjustments:', adjustments);
      setRoleAdjustments(adjustments);
    } catch (error) {
      console.error("Error fetching role adjustments:", error);
    }
  };

  const getPlayerReports = async () => {
    try {
      const response = await db
        .collection("reports")
        .where("playerProfile", "==", id)
        .get();
      let data = response.docs.map((report) => ({
        ...report.data(),
        id: report.id,
        playerName: `${playerProfileData.FirstName} ${playerProfileData.LastName}`,
      }));
      setReports(data);
      
      // Extract and count roles from reports
      const roles = [];
      const counts = {}; // Count total occurrences for display
      const reportCounts = {}; // Count unique reports per role for percentage calculation
      
      data.forEach(report => {
        if (report.typeSpecificFields?.roles && Array.isArray(report.typeSpecificFields.roles)) {
          const uniqueRolesInReport = new Set(); // Track unique roles in this report
          report.typeSpecificFields.roles.forEach(role => {
            if (role && role.trim() !== '') {
              roles.push(role);
              counts[role] = (counts[role] || 0) + 1; // Total occurrences
              uniqueRolesInReport.add(role);
            }
          });
          // Count this report once per unique role
          uniqueRolesInReport.forEach(role => {
            reportCounts[role] = (reportCounts[role] || 0) + 1;
          });
        }
      });
      
      setRolesData(roles);
      setRoleCounts(counts);
      // Store report counts for percentage calculation
      setReportCounts(reportCounts);
      
      // Fetch role explanations for roles from reports
      await fetchRoleExplanations(Object.keys(counts));
      
      // Fetch role adjustments for this player
      await fetchRoleAdjustments();
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleWatchList = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const playerDocRef = db.collection("watchlist").doc(id);
    const playerDoc = await playerDocRef.get();
    const docData = playerDoc.data();
    const subscribers = Array.isArray(docData?.subscribers) ? docData.subscribers : [];

    if (playerDoc.exists) {
      if (subscribers.includes(uid)) {
        await playerDocRef.update({
          subscribers: subscribers.filter((sub) => sub !== uid),
        });
        setIsWatchListed(false);
      } else {
        await playerDocRef.update({
          subscribers: [...subscribers, uid],
        });
        setIsWatchListed(true);
      }
    } else {
      await playerDocRef.set({
        subscribers: [uid],
      });
      setIsWatchListed(true);
    }
  };

  useEffect(() => {
    document.title = `${playerProfileData.FirstName} ${playerProfileData.LastName}`;
    getPlayerReports();
  }, []);

  // Log when role adjustments change for debugging
  useEffect(() => {
    console.log('roleAdjustments changed:', roleAdjustments);
  }, [roleAdjustments]);

  // Fetch explanations for all roles that will be displayed
  useEffect(() => {
    if (Object.keys(roleCounts).length > 0 && Object.keys(roleAdjustments).length > 0) {
      const allRoles = new Set([
        ...Object.keys(roleCounts),
        ...Object.keys(roleAdjustments)
      ]);
      
      const rolesWithCounts = Array.from(allRoles).map(role => {
        const liveCount = roleCounts[role] || 0;
        const adjustment = roleAdjustments[role] || 0;
        return { role, adjustedCount: liveCount + adjustment };
      });
      
      const rolesToDisplay = rolesWithCounts
        .filter(item => item.adjustedCount > 0)
        .map(item => item.role);
      
      // Fetch explanations for ALL roles that will be displayed, not just missing ones
      if (rolesToDisplay.length > 0) {
        console.log('Fetching explanations for all displayed roles:', rolesToDisplay);
        fetchRoleExplanations(rolesToDisplay);
      }
    }
  }, [roleCounts, roleAdjustments]); // Only depend on the data sources, not explanations

  useEffect(() => {
    setIsWatchListed(false);

    if (!id) return;

    let cancelled = false;

    async function fetchWatchList() {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const document = await db.collection("watchlist").doc(id).get();
      if (cancelled) return;

      const subs = Array.isArray(document.data()?.subscribers)
        ? document.data().subscribers
        : [];
      setIsWatchListed(document.exists && subs.includes(uid));
    }

    fetchWatchList();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const buyOutData =
    bgOverviewData && bgOverviewData.IBO
      ? {
          BuyOutInfo: {
            title: "Buy-Out Information",
            list: bgOverviewData.buyOutInfo.terms,
          },
        }
      : {};

  const Awards = bgOverviewData
    ? {
        Awards: {
          title: "Awards & Honors",
          list: bgOverviewData.awards.map((item) =>
            item.year === "" ? "" : `${item.year}: ${item.awards}`
          ),
        },
      }
    : {};

  const natioalTeamExp = bgOverviewData
    ? {
        nationalTeam: {
          title: `National Team Experience (${bgOverviewData.nationalTeamExperience.country})`,
          list: bgOverviewData.nationalTeamExperience.experiences.map((item) =>
            item.year === "" ? "" : `${item.year}: ${item.experience}`
          ),
        },
      }
    : {};

  const recruitmentData = bgOverviewData
    ? {
        NotableVisits: {
          title: "Notable Visits",
          list: bgOverviewData.recruitment.notableVisits,
        },
        NotableOffers: {
          title: "Notable Offers",
          list: bgOverviewData.recruitment.notableOffers,
        },
        NotableInterests: {
          title: "Notable Interest",
          list: bgOverviewData.recruitment.notableInterests,
        },
      }
    : {};

  const personalData = bgOverviewData
    ? {
        family: {
          title: "Family/Circle",
          list: bgOverviewData.personal.familyCircleMembers.map((item) =>
            item.member === "" ||
            item.member === "---" ||
            item.member === "--" ||
            item.member === "--:"
              ? item.note
              : `${item.member}: ${item.note}`
          ),
        },
        nugget: {
          title: "Nugget(s)",
          list: bgOverviewData.personal.nuggets,
        },
      }
    : {};

  const socialData = bgOverviewData
    ? {
        Twitter: {
          title: "Twitter",
          social: {
            tag:
              bgOverviewData.socialMedia.twitter.account === ""
                ? ""
                : `@${bgOverviewData.socialMedia.twitter.account}`,
            followers: `Followers:`,
          },
        },
        Instagram: {
          title: "Instagram",
          social: {
            tag:
              bgOverviewData.socialMedia.instagram.account === ""
                ? ""
                : `@${bgOverviewData.socialMedia.instagram.account}`,
            followers: `Followers:`,
          },
        },
      }
    : {};

  const commitmentData = bgOverviewData?.recruitment.commitment;
  const monthData = bgOverviewData?.recruitment.month;
  const yearData = bgOverviewData?.recruitment.year;

  return (
    <div className={compare ? styles.compareRoot : ""}>
      {compareModal && (
        <SelectCompareProfile
          id={id}
          handleClose={() => setCompareModal(false)}
        />
      )}

      <div className={styles.printDigestMount}>
          <div ref={printDigestRef} className={styles.printDigestRoot}>
            <div className={styles.desktopHeaderWrapper} style={{ marginTop: 0 }}>
              <Row className={`g-0 ${styles.desktopHeaderRow}`}>
                <Col {...leftHeaderColProps} className={styles.desktopHeaderLeft}>
                  <div className={styles.desktopHeaderLeftInner}>
                    <div className={styles.desktopLeftContent}>
                      <div className={styles.desktopPlayerImageWrap}>
                        <img
                          data-print-headshot
                          src={printDigestPhotoSrc}
                          alt={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
                          className={styles.desktopPlayerImage}
                          crossOrigin={printDigestHeadshotCrossOrigin}
                          onError={(e) => {
                            const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
                            e.currentTarget.src = `${window.location.origin}${publicUrl}/logo512.png`;
                          }}
                        />
                      </div>

                      <div className={styles.desktopPlayerDetails}>
                        <h2 className={styles.desktopPlayerName}>
                          {playerProfileData.FirstName}
                          <br />
                          {playerProfileData.LastName}
                        </h2>

                        <div className={styles.desktopHeaderFieldBlock}>
                          <div className={styles.desktopHeaderFieldLabel}>Hometown</div>
                          <div className={styles.desktopHeaderFieldValue}>{hometownDisplayText}</div>
                        </div>

                        <div className={styles.desktopHeaderFieldBlock}>
                          <div className={styles.desktopHeaderFieldLabel}>Current Team</div>
                          <div className={styles.desktopHeaderFieldValue}>{currentTeamDesktopText}</div>
                        </div>

                        <div className={styles.desktopHeaderFieldBlock}>
                          <div className={styles.desktopHeaderFieldLabel}>AAU Team</div>
                          <div className={styles.desktopHeaderFieldValue}>{aauTeamText || "-"}</div>
                        </div>

                        <div className={styles.desktopHeaderFieldBlock}>
                          <div className={styles.desktopHeaderFieldLabel}>Recruitment Status</div>
                          <div className={styles.desktopHeaderFieldValue}>{recruitmentStatusText || "-"}</div>
                        </div>

                        <div className={styles.desktopHeaderFieldBlock}>
                          <div className={styles.desktopHeaderFieldLabel}>Projection Range</div>
                          <div className={styles.desktopHeaderFieldValue}>{projectionRangeText}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col {...rightHeaderColProps} className={styles.desktopStatsColumn}>
                  <div className={styles.desktopStatsColumnInner}>
                    <div className={styles.desktopStatsRow}>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>HEIGHT</div>
                        <div className={styles.desktopStatValue}>
                          {formatFeetInches(playerProfileData.Height) || formatStatValue(playerProfileData.Height)}
                        </div>
                      </div>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>WEIGHT</div>
                        <div className={styles.desktopStatValue}>{formatWeightDisplay(playerProfileData.Weight, "-")}</div>
                      </div>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>WINGSPAN</div>
                        <div className={styles.desktopStatValue}>
                          {formatFeetInches(playerProfileData.Wingspan) || "-"}
                        </div>
                      </div>
                    </div>

                    <div className={styles.desktopStatsRow}>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>HS CLASS</div>
                        <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Year)}</div>
                      </div>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>HAND</div>
                        <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Hand)}</div>
                      </div>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>POSITION</div>
                        <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Position)}</div>
                      </div>
                    </div>

                    <div className={styles.desktopStatsRow}>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>DATE OF BIRTH</div>
                        <div className={styles.desktopStatValue}>{dobText}</div>
                      </div>
                      <div className={styles.desktopStatCard}>
                        <div className={styles.desktopStatLabel}>AGE</div>
                        <div className={styles.desktopStatValue}>{ageText}</div>
                      </div>
                      <div className={`${styles.desktopStatCard} ${styles.desktopStatCardBlack}`}>
                        <div className={styles.desktopStatLabelBlack}>P.I. SCORE</div>
                        <div className={styles.desktopStatValueBlack}>{piScoreText}</div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {hasCompositeRankings ? (
              <div className={`${styles.printIndustryRankings} print-section-lead`}>
                <div className={`${styles.printIndustryRankingsTitle} print-section-title`}>
                  Industry rankings
                </div>
                <div className={styles.printIndustryRankingsRow}>
                  {compositeRankingItems.map((item) => {
                    const rankText = formatCompositeRankDisplay(item.value);
                    return (
                      <div key={item.key} className={styles.printIndustryRankingsItem}>
                        <span className={styles.printIndustryRankingsLabel}>{item.label}</span>
                        <span className={styles.printIndustryRankingsValue}>{rankText}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {bgOverviewData?.teamHistory?.length > 0 ? (
              <div className={styles.printSection}>
                <div className="print-section-lead">
                  <h2 className={`${styles.printSectionTitle} print-section-title`}>Team History</h2>
                  <div className={styles.table}>
                  <table className={styles.printTeamHistoryTable}>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Team</th>
                        <th>Location</th>
                        <th>Coach</th>
                        <th>Phone</th>
                        <th>e-mail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bgOverviewData.teamHistory.map((th) => (
                        <tr key={`${th.year}-${th.school || th.team}-${th.coachLastName || ""}`}>
                          <td>{th.year}</td>
                          <td>{th.school || th.team}</td>
                          <td>{th.country || th.state}</td>
                          <td>
                            {th.coachFirstName} {th.coachLastName}
                          </td>
                          <td>{th.phone}</td>
                          <td>{th.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>
            ) : null}

            {showRecruitmentPrintSection ? (
              <div className={styles.printSection}>
                <h2 className={`${styles.printSectionTitle} print-section-title`}>Recruitment</h2>
                {commitmentPrint ? (
                  <div className={styles.printTextRow}>
                    <strong>Commitment:</strong>&nbsp;{bgOverviewData.recruitment.commitment}
                  </div>
                ) : null}
                {visitsPrint.length > 0 ? (
                  <>
                    <div className={styles.printSubhead}>Notable Visits</div>
                    {printCommaListFromStrings(visitsPrint)}
                  </>
                ) : null}
                {offersPrint.length > 0 ? (
                  <>
                    <div className={styles.printSubhead}>Notable Offers</div>
                    {printCommaListFromStrings(offersPrint)}
                  </>
                ) : null}
                {interestsPrint.length > 0 ? (
                  <>
                    <div className={styles.printSubhead}>Notable Interests</div>
                    {printCommaListFromStrings(interestsPrint)}
                  </>
                ) : null}
                {hasRecruitmentNotesPrint ? (
                  <>
                    <div className={styles.printSubhead}>Notes</div>
                    {printRecruitmentNotesPlain(bgOverviewData.recruitment.notes)}
                  </>
                ) : null}
              </div>
            ) : null}

            {hasFamilyCirclePrint ? (
              <div className={styles.printSection}>
                <div className="print-section-lead">
                  <h2 className={`${styles.printSectionTitle} print-section-title`}>Family / Circle</h2>
                  <div className={styles.printBulletBlockList}>
                  {familyCirclePrintLines.map((t, idx) => (
                    <div key={`${idx}-${t.slice(0, 48)}`} className="print-bullet-row">
                      <span className="print-bullet-glyph" aria-hidden="true">
                        •
                      </span>
                      <span className="print-bullet-text">{t}</span>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            ) : null}

            {hasNuggetsPrint ? (
              <div className={styles.printSection}>
                <div className="print-section-lead">
                  <h2 className={`${styles.printSectionTitle} print-section-title`}>Nuggets</h2>
                  <div className={styles.printBulletBlockList}>
                  {nuggetsPrintRows.map((row) => (
                    <div key={row.key} className="print-bullet-row">
                      {row.showBullet ? (
                        <span className="print-bullet-glyph" aria-hidden="true">
                          •
                        </span>
                      ) : (
                        <span className="print-bullet-glyph print-bullet-spacer" aria-hidden="true">
                          •
                        </span>
                      )}
                      <span className="print-bullet-text">{row.text}</span>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            ) : null}

            {hasSocialsPrintSection ? (
              <div className={styles.printSection}>
                <div className="print-section-lead">
                  <h2 className={`${styles.printSectionTitle} print-section-title`}>Socials</h2>
                {hasInstagramPrintBlock ? (
                  <>
                    <div className={styles.printSubhead}>Instagram</div>
                    {instagramHandlePrint ? (
                      <div className={styles.printTextRow}>@{instagramHandlePrint}</div>
                    ) : null}
                    {instagramAccountBg ? (
                      <div className={styles.printTextRow}>
                        <strong>Account is:</strong>&nbsp;{instagramAccountBg}
                      </div>
                    ) : null}
                    {instagramNuggetsPrintLines.length > 0 ? (
                      <div className={styles.printBulletBlockList}>
                        {instagramNuggetsPrintLines.map((t, idx) => (
                          <div key={`${idx}-${t.slice(0, 48)}`} className="print-bullet-row">
                            <span className="print-bullet-glyph" aria-hidden="true">
                              •
                            </span>
                            <span className="print-bullet-text">{t}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
                {hasTwitterPrintBlock ? (
                  <>
                    <div className={styles.printSubhead}>Twitter</div>
                    {twitterHandlePrint ? (
                      <div className={styles.printTextRow}>@{twitterHandlePrint}</div>
                    ) : null}
                    {twitterAccountBg ? (
                      <div className={styles.printTextRow}>
                        <strong>Account is:</strong>&nbsp;{twitterAccountBg}
                      </div>
                    ) : null}
                    {twitterDmsPrint ? (
                      <div className={styles.printTextRow}>
                        <strong>DMs are:</strong>&nbsp;{twitterDmsPrint}
                      </div>
                    ) : null}
                    {twitterNuggetsPrintLines.length > 0 ? (
                      <div className={styles.printBulletBlockList}>
                        {twitterNuggetsPrintLines.map((t, idx) => (
                          <div key={`${idx}-${t.slice(0, 48)}`} className="print-bullet-row">
                            <span className="print-bullet-glyph" aria-hidden="true">
                              •
                            </span>
                            <span className="print-bullet-text">{t}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
                {snapchatPrint ? (
                  <div className={styles.printTextRow}>
                    <strong>Snapchat:</strong>&nbsp;@{snapchatPrint.replace(/^@+/, "")}
                  </div>
                ) : null}
                {tiktokPrint ? (
                  <div className={styles.printTextRow}>
                    <strong>TikTok:</strong>&nbsp;@{tiktokPrint.replace(/^@+/, "")}
                  </div>
                ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>

      {/* --- MOBILE HEADER --- */}
      <div className="d-block d-lg-none">
        <div className={styles.mobileLayoutHeader}>
          {/* Back Button + Image */}
          <IoChevronBackCircleOutline
              onClick={() => history.goBack()}
              style={{ cursor: "pointer", fontSize: "1.8rem", marginRight: "8px" }}
            />
          <div className={styles.mobileHeaderTop}>
            
            <div className={styles.playerImageContainer}>
              <img
                src={playerProfileData.Headshot && playerProfileData.Headshot.trim() !== "" && playerProfileData.Headshot !== "null" ? playerProfileData.Headshot : defaultImage}
                alt="Headshot"
                className={styles.playerImageMobile}
                onError={(e) => { e.target.src = defaultImage; }}
              />
            </div>
          </div>

          {/* Name, Hometown, Team */}
          <div className={styles.playerInfoMobile}>
            <h2 className={styles.playerNameMobile}>
              {playerProfileData.FirstName} {playerProfileData.LastName}
            </h2>
            <div className={styles.mobileFieldBlock}>
              <div className={styles.mobileFieldLabel}>Hometown</div>
              <div className={styles.mobileFieldValue}>{hometownDisplayText}</div>
            </div>
            <div className={styles.mobileFieldBlock}>
              <div className={styles.mobileFieldLabel}>Current Team</div>
              <div className={styles.mobileFieldValue}>{currentTeamMobileText}</div>
            </div>
          </div>

          {/* Additional Info: AAU Team and Status */}
          <div className={styles.additionalInfoMobile}>
            {playerProfileData.aauTeam && (
              <p className={styles.aauTeamMobile}>{playerProfileData.aauTeam}</p>
            )}
            {playerProfileData.Status && (
              <p className={styles.playerStatusMobile}>
                {playerProfileData.Status !== "Undecided"
                  ? `${playerProfileData.Status}: ${playerProfileData.committedTeam}`
                  : playerProfileData.Status}
              </p>
            )}
          </div>

          {/* Stats Grid */}
          <div className={styles.projectionMobile}>
              <p>Projection Range</p>
              <h4>
                {playerProfileData.pRange &&
                playerProfileData.Prlow !== "" &&
                playerProfileData.Prhigh !== ""
                  ? playerProfileData.Prlow === playerProfileData.Prhigh
                    ? playerProfileData.Prhigh
                    : `${playerProfileData.Prlow} to ${playerProfileData.Prhigh}`
                  : "-"}
              </h4>
            </div>


          <div className={styles.statsGridMobile}>
            <div className={styles.statItemMobile}>
              <span>HT</span>
              <strong>{formatFeetInches(playerProfileData.Height) || "--"}</strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>WT</span>
              <strong>
                {formatWeightDisplay(playerProfileData.Weight)}
              </strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>WS</span>
              <strong>
                {formatFeetInches(playerProfileData.Wingspan) || "--"}
              </strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>CLASS</span>
              <strong>{playerProfileData.Year || "--"}</strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>HAND</span>
              <strong>{playerProfileData.Hand || "--"}</strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>POS</span>
              <strong>{playerProfileData.Position || "--"}</strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>DOB</span>
              <strong>
                {playerProfileData.DOB
                  ? moment(playerProfileData.DOB).format("MM/DD/YY")
                  : "--"}
              </strong>
            </div>
            <div className={styles.statItemMobile}>
              <span>AGE</span>
              <strong>
                {formatProfileAge(playerProfileData.DOB, playerProfileData.Age)}
              </strong>
            </div>
            <div className={`${styles.statItemMobile} ${styles.piScoreMobile}`}>
              <span style={{ color: "white" }}>P.I. SCORE</span>
              <strong style={{ color: "white", textAlign: "center", fontSize: "14px" }}>
                {(isAdmin() || isSuperAdmin()) && playerProfileData.overallScore
                  ? playerProfileData.overallScore
                  : "Coming Soon"}
              </strong>
            </div>
          </div>

          {hasCompositeRankings && (
            <div className={styles.compositeRankingsMobile}>
              <p className={styles.compositeRankingsMobileTitle}>Industry Rankings</p>
              <div className={styles.compositeRankingsMobileList}>
                {compositeRankingItems.map((item) => {
                  const rankText = formatCompositeRankDisplay(item.value);
                  return (
                    <div key={item.key} className={styles.compositeRankingsMobileItem}>
                      <span className={styles.compositeRankingsMobileLabel}>{item.label}</span>
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.compositeRankingsMobileLink}
                        >
                          {rankText}
                        </a>
                      ) : (
                        <strong className={styles.compositeRankingsMobileRank}>{rankText}</strong>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

    
        </div>

        {/* Compare & Watchlist Buttons (Mobile) */}
        <div className={styles.compareSectionMobile}>
          <Row>
            <Col xs={12}>
              <button
                className={styles.watchButtonMobile}
                onClick={handleWatchList}
              >
                {isWatchListed ? (
                  <>
                    <IoStarSharp />
                    <span>Remove from Watchlist</span>
                  </>
                ) : (
                  <>
                    <IoStarOutline />
                    <span>Add to Watchlist</span>
                  </>
                )}
              </button>
            </Col>
          </Row>
        </div>
      </div>
      {/* --- END MOBILE HEADER --- */}

      {/* --- DESKTOP HEADER --- */}
      <div className="d-none d-lg-block">
        <div className={`${styles.desktopHeaderWrapper} ${compare ? styles.compareDesktopHeader : ""}`}>
          <Row className={`g-0 ${styles.desktopHeaderRow}`}>
            <Col {...leftHeaderColProps} className={styles.desktopHeaderLeft}>
              <div className={styles.desktopHeaderLeftInner}>
              {!compare && (
                <div className={styles.desktopBackRow}>
                  <button
                    type="button"
                    className={styles.desktopBackButton}
                    onClick={() => history.goBack()}
                  >
                    <IoChevronBack className={styles.desktopBackIcon} />
                  </button>
                </div>
              )}

              <div className={styles.desktopLeftContent}>
                <div className={styles.desktopPlayerImageWrap}>
                  <img
                    src={
                      playerProfileData.Headshot &&
                      playerProfileData.Headshot.trim() !== "" &&
                      playerProfileData.Headshot !== "null"
                        ? playerProfileData.Headshot
                        : defaultImage
                    }
                    alt={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
                    className={styles.desktopPlayerImage}
                    onError={(e) => {
                      e.currentTarget.src = defaultImage;
                    }}
                  />
                </div>

                <div className={styles.desktopPlayerDetails}>
                  <h2 className={styles.desktopPlayerName}>
                    {playerProfileData.FirstName}
                    <br />
                    {playerProfileData.LastName}
                  </h2>

                  <div className={styles.desktopHeaderFieldBlock}>
                    <div className={styles.desktopHeaderFieldLabel}>Hometown</div>
                    <div className={styles.desktopHeaderFieldValue}>{hometownDisplayText}</div>
                  </div>

                  <div className={styles.desktopHeaderFieldBlock}>
                    <div className={styles.desktopHeaderFieldLabel}>Current Team</div>
                    <div className={styles.desktopHeaderFieldValue}>{currentTeamDesktopText}</div>
                  </div>

                  <div className={styles.desktopHeaderFieldBlock}>
                    <div className={styles.desktopHeaderFieldLabel}>AAU Team</div>
                    <div className={styles.desktopHeaderFieldValue}>{aauTeamText || "-"}</div>
                  </div>

                  <div className={styles.desktopHeaderFieldBlock}>
                    <div className={styles.desktopHeaderFieldLabel}>Recruitment Status</div>
                    <div className={styles.desktopHeaderFieldValue}>{recruitmentStatusText || "-"}</div>
                  </div>

                  <div className={styles.desktopHeaderFieldBlock}>
                    <div className={styles.desktopHeaderFieldLabel}>Projection Range</div>
                    <div className={styles.desktopHeaderFieldValue}>{projectionRangeText}</div>
                  </div>
                </div>
              </div>
            </div>
            </Col>

            <Col {...rightHeaderColProps} className={styles.desktopStatsColumn}>
              <div className={styles.desktopStatsColumnInner}>
              <div className={styles.desktopStatsRow}>
                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>HEIGHT</div>
                  <div className={styles.desktopStatValue}>
                    {formatFeetInches(playerProfileData.Height) || formatStatValue(playerProfileData.Height)}
                  </div>
                </div>

                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>WEIGHT</div>
                  <div className={styles.desktopStatValue}>{formatWeightDisplay(playerProfileData.Weight, "-")}</div>
                </div>

                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>WINGSPAN</div>
                  <div className={styles.desktopStatValue}>
                    {formatFeetInches(playerProfileData.Wingspan) || "-"}
                  </div>
                </div>
              </div>

              <div className={styles.desktopStatsRow}>
                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>HS CLASS</div>
                  <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Year)}</div>
                </div>

                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>HAND</div>
                  <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Hand)}</div>
                </div>

                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>POSITION</div>
                  <div className={styles.desktopStatValue}>{formatStatValue(playerProfileData.Position)}</div>
                </div>
              </div>

              <div className={styles.desktopStatsRow}>
                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>DATE OF BIRTH</div>
                  <div className={styles.desktopStatValue}>{dobText}</div>
                </div>

                <div className={styles.desktopStatCard}>
                  <div className={styles.desktopStatLabel}>AGE</div>
                  <div className={styles.desktopStatValue}>{ageText}</div>
                </div>

                <div className={`${styles.desktopStatCard} ${styles.desktopStatCardBlack}`}>
                  <div className={styles.desktopStatLabelBlack}>P.I. SCORE</div>
                  <div className={styles.desktopStatValueBlack}>{piScoreText}</div>
                </div>
              </div>
              </div>
            </Col>
          </Row>

          {hasCompositeRankings && (
            <div className={styles.compositeRankingsDesktopStrip}>
              <div className={styles.compositeRankingsDesktopRowAll}>
                <div className={styles.compositeRankingsDesktopTitle}>INDUSTRY RANKINGS</div>
                <div className={styles.compositeRankingsDesktopInlineItems}>
                  {compositeRankingItems.map((item) => {
                    const rankText = formatCompositeRankDisplay(item.value);
                    return (
                      <div key={item.key} className={styles.compositeRankingsDesktopInlineItem}>
                        <span className={styles.compositeRankingsDesktopLabel}>{item.label}</span>
                        {item.url ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.compositeRankingsDesktopRankLink}
                          >
                            {rankText}
                          </a>
                        ) : (
                          <span className={styles.compositeRankingsDesktopRank}>{rankText}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className={styles.compareSection}>
            <Row className="g-3">
              {!compare && (
                <Col md={4}>
                  <button onClick={() => setCompareModal(true)}>
                    <IoSwapHorizontalOutline />
                    <span>Compare</span>
                  </button>
                </Col>
              )}
              <Col md={compare ? 6 : 4}>
                <button className={styles.watchButton} onClick={handleWatchList}>
                  {isWatchListed ? (
                    <>
                      <IoStarSharp />
                      <span>Remove from Watchlist</span>
                    </>
                  ) : (
                    <>
                      <IoStarOutline />
                      <span>Add to Watchlist</span>
                    </>
                  )}
                </button>
              </Col>
              <Col md={compare ? 6 : 4}>
                <button
                  className={styles.printButton}
                  onClick={handlePrintDigest}
                  disabled={isPrintingDigest}
                >
                  {isPrintingDigest ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}>
                        PRINTING
                      </span>
                    </>
                  ) : (
                    <>
                      <IoPrintOutline />
                      <span>PRINT</span>
                    </>
                  )}
                </button>
              </Col>
            </Row>
          </div>
        </div>
      </div>
      {/* --- END DESKTOP HEADER --- */}

      {/* Accordion Sections — multiple panels can stay open */}
      <Accordion activeKey={accordionActiveKeys} onSelect={handleAccordionSelect} className={styles.accordionSection}>
        {canViewMeasurementsAndKeyContacts && keyContacts.length > 0 && (
          <Accordion.Item eventKey="keyContacts">
            <Accordion.Header>KEY CONTACTS</Accordion.Header>
            <Accordion.Body>
              <div className={directoryStyles.container}>
                <div className={directoryStyles.table}>
                  <div
                    className={`${directoryStyles.row} ${directoryStyles.row6col} ${directoryStyles.headerRow}`}
                  >
                    <div>Contact</div>
                    <div>Full Name</div>
                    <div>Cell</div>
                    <div>Email</div>
                    <div>Notes</div>
                  </div>
                  {keyContacts.map((c) => (
                    <div key={c.id} className={`${directoryStyles.row} ${directoryStyles.row6col}`}>
                      <div>{c.relation || "--"}</div>
                      <div>{c.fullName || "--"}</div>
                      <div>{c.cell || "--"}</div>
                      <div>{c.email || "--"}</div>
                      <div className={directoryStyles.notesCell}>{c.notes || "--"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}

        {bgOverviewData && (
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              {bgOverviewData.IBO
                ? "INT'L BACKGROUND OVERVIEW"
                : "BACKGROUND OVERVIEW"}
            </Accordion.Header>
            <Accordion.Body>
              {bgOverviewData.teamHistory?.length > 0 && (
                <>
                  <BodyTitle title="Playing History" />
                  <h2 className={styles.grayTitle}>Team History</h2>
                  <div className={styles.table}>
                    <table>
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Team</th>
                          <th>Location</th>
                          <th>Coach</th>
                          <th>Phone</th>
                          <th>e-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bgOverviewData.teamHistory.map((th) => (
                          <tr>
                            <td>{th.year}</td>
                            <td>{th.school || th.team}</td>
                            <td>{th.country || th.state}</td>
                            <td>
                              {th.coachFirstName} {th.coachLastName}
                            </td>
                            <td>{th.phone}</td>
                            <td>{th.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {playerProfileData.aauTeam && (
                <>
                  <h2 className={styles.grayTitle}>AAU</h2>
                  <div className={styles.table}>
                    <table>
                      <thead>
                        <tr>
                          <th colSpan={4}>Team</th>
                          <th colSpan={2}></th>
                          <th>Affiliation</th>
                          <th>Coach</th>
                          <th>Phone</th>
                          <th>e-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={4}>{playerProfileData.aauTeam}</td>
                          <td colSpan={2}></td>
                          <td>{playerProfileData.aauAffil}</td>
                          <td>
                            {playerProfileData.aauCoach}{" "}
                            {playerProfileData.aauCoachLast}
                          </td>
                          <td>{playerProfileData.aauCoachInfo}</td>
                          <td>{playerProfileData.aauCoachEmail}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <BodyColList data={natioalTeamExp} />
              <BodyColList data={Awards} />

              {bgOverviewData.IBO && bgOverviewData.buyOutInfo?.terms?.length > 0 ? (
                <>
                  <BodyTitle title="Buy-Out Information" />
                  <BodyList data={buyOutData} cols={2} />
                </>
              ) : (
                <>
                  {(commitmentData ||
                    bgOverviewData.recruitment.notableVisits.some(
                      (item) => item !== ""
                    ) ||
                    bgOverviewData.recruitment.notableOffers.some(
                      (item) => item !== ""
                    ) ||
                    bgOverviewData.recruitment.notableInterests.some(
                      (item) => item !== ""
                    ) ||
                    bgOverviewData.recruitment.notes) && (
                    <div className={styles.recruitmentContent}>
                      <BodyTitle title="Recruitment" />
                    </div>
                  )}

                  {commitmentData ? (
                    <div className={styles.recruitmentContent}>
                      <h2 className={styles.grayTitle}>Commitment</h2>
                      <div className={styles.table}>
                        <table>
                          <thead>
                            <tr>
                              <th>Team</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{commitmentData}</td>
                              <td>
                                {monthData} {yearData}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  {bgOverviewData.recruitment.notableVisits.length > 0 && (
                    <span className={styles.recruitmentContent}>
                      <h2 className={styles.grayTitle}>Visits</h2>
                      <Row>
                        <h5
                          className={styles.colListDesc}
                          style={{ fontWeight: 600, fontSize: isMobileView ? '14px' : '21px', display: 'flex', alignItems: 'flex-start' }}
                        >
                          <span style={{ marginRight: '10px', marginLeft: '20px', flexShrink: 0 }} role="img" aria-label="bulb">💡</span>
                          <span style={{ display: 'block' }}>
                            {bgOverviewData.recruitment.notableVisits
                              .filter((item) => item !== "")
                              .join(", ")}
                          </span>
                        </h5>
                      </Row>
                    </span>
                  )}

                  {bgOverviewData.recruitment.notableOffers.length > 0 && (
                    <span className={styles.recruitmentContent}>
                      <h2 className={styles.grayTitle}>Offers</h2>
                      <Row>
                        <h5
                          className={styles.colListDesc}
                          style={{ fontWeight: 600, fontSize: isMobileView ? '14px' : '21px', display: 'flex', alignItems: 'flex-start' }}
                        >
                          <span style={{ marginRight: '10px', marginLeft: '20px', flexShrink: 0 }} role="img" aria-label="bulb">💡</span>
                          <span style={{ display: 'block' }}>
                            {bgOverviewData.recruitment.notableOffers
                              .filter((item) => item !== "")
                              .join(", ")}
                          </span>
                        </h5>
                      </Row>
                    </span>
                  )}

                  {bgOverviewData.recruitment.notableInterests.length > 0 && (
                    <span className={styles.recruitmentContent}>
                      <h2 className={styles.grayTitle}>Interest</h2>
                      <Row>
                        <h5
                          className={styles.colListDesc}
                          style={{ fontWeight: 600, fontSize: isMobileView ? '14px' : '21px', display: 'flex', alignItems: 'flex-start' }}
                        >
                          <span style={{ marginRight: '10px', marginLeft: '20px', flexShrink: 0 }} role="img" aria-label="bulb">💡</span>
                          <span style={{ display: 'block' }}>
                            {bgOverviewData.recruitment.notableInterests
                              .filter((item) => item !== "")
                              .join(", ")}
                          </span>
                        </h5>
                      </Row>
                    </span>
                  )}

                  {bgOverviewData.recruitment.notes && (
                    <div className={styles.recruitmentContent}>
                      <h2 className={styles.grayTitle}>Notes</h2>
                      <div
                        style={{
                          marginLeft: "20px",
                          fontSize: isMobileView ? "14px" : "21px",
                          fontWeight: isMobileView ? "600" : "500",
                          lineHeight: "1.5",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {bgOverviewData.recruitment.notes}
                      </div>
                    </div>
                  )}
                </>
              )}

              {(personalData.family?.list?.length > 0 ||
                personalData.nugget?.list?.length > 0) && (
                <>
                  <BodyTitle title="Personal" />
                  <BodyList data={personalData} />
                </>
              )}

              {bgOverviewData.additionalNotes && (
                <>
                  <BodyTitle title="Notes" />
                  <div
                      style={{
                        marginLeft: "20px",
                        fontSize: isMobileView ? "14px" : "21px",
                        fontWeight: isMobileView ? "600" : "500",
                        lineHeight: "1.5",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                  
                    {bgOverviewData.additionalNotes}
                  </div>
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )}
        {scoutingOverviewData && (
          <Accordion.Item eventKey="3">
            <Accordion.Header>SCOUTING OVERVIEW</Accordion.Header>
            <Accordion.Body>
              {(scoutingOverviewData.projections.Projections.list.length > 0 ||
                scoutingOverviewData.roles.Roles.list.length > 0) && (
                <>
                  <BodyTitle title="Outlook" />
                  {scoutingOverviewData.projections.Projections.list.length >
                    0 && <BodyList data={scoutingOverviewData.projections} />}
                  {scoutingOverviewData.roles.Roles.list.length > 0 && (
                    <BodyList data={scoutingOverviewData.roles} />
                  )}
                </>
              )}

              {scoutingOverviewData.strengths.Strengths.list.length > 0 && (
                <>
                  <BodyTitle title="Strengths" />
                  <BodyList data={scoutingOverviewData.strengths} />
                </>
              )}
              {scoutingOverviewData.weaknesses.Weaknesses.list.length > 0 && (
                <>
                  <BodyTitle title="Weaknesses" />
                  <BodyList data={scoutingOverviewData.weaknesses} />
                </>
              )}
              {scoutingOverviewData.bottomLine.length > 0 && (
                <>
                  <BodyTitle title="Bottom Line" />
                  <div
                    style={{
                      marginLeft: "20px",
                      fontSize: isMobileView ? "14px" : "21px",
                      fontWeight: "600",
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {scoutingOverviewData.bottomLine}
                  </div>
                </>
              )}
              {scoutingOverviewData.questions.Questions.list.length > 0 && (
                <>
                  <BodyTitle title="Questions" />
                  <BodyList data={scoutingOverviewData.questions} />
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )}

        {canViewMeasurementsAndKeyContacts && measurements.length > 0 && (() => {
          const showHeightNotSpecified = measurements.some(
            (m) => m.heightNotSpecified != null && String(m.heightNotSpecified).trim() !== ""
          );
          return (
          <Accordion.Item eventKey="measurements">
            <Accordion.Header>MEASUREMENTS / TESTING</Accordion.Header>
            <Accordion.Body>
              <div className={detailStyles.container}>
                <div className={detailStyles.feed}>
                  <div className={detailStyles.tableContainer}>
                  <table className={detailStyles.measurementsTable}>
                    <thead>
                      <tr>
                        <th rowSpan="2">Event Date</th>
                        <th rowSpan="2">Event</th>
                        <th rowSpan="2">Age</th>
                        <th colSpan={showHeightNotSpecified ? 3 : 2}>Height</th>
                        <th rowSpan="2">Weight</th>
                        <th rowSpan="2">Wingspan</th>
                        <th rowSpan="2" className={detailStyles.tableHeaderTwoLine}>Standing<br />Reach</th>
                        <th colSpan="2">Vertical Jump</th>
                        <th rowSpan="2" className={detailStyles.tableHeaderTwoLine}>Body<br />Fat %</th>
                        <th colSpan="2">Hand</th>
                        <th rowSpan="2" className={detailStyles.tableHeaderTwoLine}>Lane<br />Agility (sec.)</th>
                        <th rowSpan="2" className={detailStyles.tableHeaderTwoLine}>Shuttle<br />(sec.)</th>
                        <th rowSpan="2" className={detailStyles.tableHeaderTwoLine}>Sprint<br />(sec.)</th>
                      </tr>
                      <tr>
                        <th>w/ shoes</th>
                        <th>w/o shoes</th>
                        {showHeightNotSpecified && (
                          <th className={detailStyles.tableHeaderTwoLine}>w/ or w/o shoes not specified*</th>
                        )}
                        <th>Max</th>
                        <th>No Step</th>
                        <th>Length</th>
                        <th>Width</th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map((m) => (
                        <tr key={m.id}>
                          <td>{formatMeasurementDate(m.date)}</td>
                          <td>{(m.event ?? m.source) || "--"}</td>
                          <td>{m.age || "--"}</td>
                          <td>{formatMeasurementFeetInches(m.heightShoes)}</td>
                          <td>{formatMeasurementFeetInches(m.heightNone)}</td>
                          {showHeightNotSpecified && (
                            <td>{formatMeasurementFeetInches(m.heightNotSpecified)}</td>
                          )}
                          <td>{formatWeightDisplay(m.weight)}</td>
                          <td>{formatMeasurementFeetInches(m.wingspan)}</td>
                          <td>{formatMeasurementFeetInches(m.standingReach)}</td>
                          <td>{m.vertMax || "--"}</td>
                          <td>{m.vertNoStep || "--"}</td>
                          <td>{m.bodyFat != null && m.bodyFat !== "" ? (String(m.bodyFat).includes("%") ? m.bodyFat : `${m.bodyFat}%`) : "--"}</td>
                          <td>{m.handL || "--"}</td>
                          <td>{m.handW || "--"}</td>
                          <td>{(m.laneAgility ?? m.handLA) || "--"}</td>
                          <td>{(m.shuttle ?? m.shuffle) || "--"}</td>
                          <td>{m.sprint || "--"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          );
        })()}

        {hasSocialMediaSection && (
          <Accordion.Item eventKey="4">
            <Accordion.Header>SOCIAL MEDIA</Accordion.Header>
            <Accordion.Body>
              {Boolean(instagramHandlePrint) && (
                <>
                  <BodyTitle title="Instagram" />
                  <BodyColList
                    data={{
                      Instagram: {
                        list: [
                          <a
                            href={`https://www.instagram.com/${instagramHandlePrint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "black" }}
                          >
                            @{instagramHandlePrint}
                          </a>,
                          bgOverviewData?.socialMedia?.instagram?.account &&
                            `Account is: ${bgOverviewData.socialMedia.instagram.account}`,
                        ],
                      },
                    }}
                    cols={3}
                    noIcon
                  />
                  <BodyList
                    data={{
                      Nuggets: {
                        list: bgOverviewData?.socialMedia?.instagram?.nuggets ?? [],
                      },
                    }}
                  />
                </>
              )}
              {Boolean(twitterHandlePrint) && (
                <>
                  <BodyTitle title="Twitter" />
                  <BodyColList
                    data={{
                      Twitter: {
                        list: [
                          <a
                            href={`https://www.twitter.com/${twitterHandlePrint}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "black" }}
                          >
                            @{twitterHandlePrint}
                          </a>,
                          bgOverviewData?.socialMedia?.twitter?.account &&
                            `Account is: ${bgOverviewData.socialMedia.twitter.account}`,
                          bgOverviewData?.socialMedia?.twitter?.dms !== "" &&
                            bgOverviewData?.socialMedia?.twitter?.dms != null &&
                            `DMs are: ${bgOverviewData.socialMedia.twitter.dms}`,
                        ],
                      },
                    }}
                    cols={3}
                    noIcon
                  />
                  <BodyList
                    data={{
                      Nuggets: {
                        list: bgOverviewData?.socialMedia?.twitter?.nuggets ?? [],
                      },
                    }}
                  />
                </>
              )}
              {Boolean(snapchatPrint) && (
                <>
                  <BodyTitle title="Snapchat" />
                  <BodyColList
                    data={{
                      Snapchat: {
                        list: [
                          <p>@{snapchatPrint.replace(/^@+/, "")}</p>
                        ],
                      },
                    }}
                    cols={4}
                    noIcon
                  />
                </>
              )}
              {Boolean(tiktokPrint) && (
                <>
                  <BodyTitle title="TikTok" />
                  <BodyColList
                    data={{
                      TikTok: {
                        list: [
                          <a
                            href={`https://www.tiktok.com/@${tiktokPrint.replace(/^@+/, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "black" }}
                          >
                            @{tiktokPrint.replace(/^@+/, "")}
                          </a>,
                        ],
                      },
                    }}
                    cols={3}
                    noIcon
                  />
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )}

        {reportsData.length > 0 && (
          <Accordion.Item eventKey="5">
            <Accordion.Header>REPORTS</Accordion.Header>
            <Accordion.Body>
              <PlayerReports
                playerID={id}
                reportData={reportsData}
                playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}

        {/* Data visualization — projection ranges over time from scouting reports */}
        {reportsData.some(
          (data) =>
            data.playerProfile === id &&
            data.type === "Scouting" &&
            !!(data.typeSpecificFields?.projectionRange?.low || data.typeSpecificFields?.projectionRange?.high)
        ) && (
          <Accordion.Item eventKey="dataViz">
            <Accordion.Header>DATA VISUALIZATION</Accordion.Header>
            <Accordion.Body>
              <h2 className={styles.dataVizSectionTitle}>Projection Ranges</h2>
              <p className={styles.dataVizHelperText}>
                Plotted over time from every scouting report entered by our staff
              </p>
              <DataVizProjectionChart
                playerID={id}
                playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
                reportsData={reportsData}
                firstName={playerProfileData.FirstName}
                lastName={playerProfileData.LastName}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}

        {(() => {
          // Check if there are any roles to display (either from reports or adjustments)
          const allRoles = new Set([
            ...Object.keys(roleCounts),
            ...Object.keys(roleAdjustments)
          ]);
          
          // Calculate total number of scouting reports
          const totalScoutingReports = reportsData.filter(report => report.type === "Scouting").length;
          
          const rolesWithCounts = Array.from(allRoles).map(role => {
            const liveCount = roleCounts[role] || 0;
            const adjustment = roleAdjustments[role] || 0;
            const adjustedCount = liveCount + adjustment;
            // Calculate percentage: (number of unique reports containing role) / (total scouting reports) * 100
            // This ensures percentage never exceeds 100% even if a role appears multiple times in the same report
            const reportsWithRole = reportCounts[role] || 0;
            const percentage = totalScoutingReports > 0 
              ? Math.min(((reportsWithRole / totalScoutingReports) * 100), 100).toFixed(0)
              : 0;
            return { role, liveCount, adjustment, adjustedCount, percentage };
          });
          
          const rolesToDisplay = rolesWithCounts.filter(item => item.adjustedCount > 0);
          
          // Debug logging
          console.log('Roles Debug:', {
            roleCounts,
            roleAdjustments,
            allRoles: Array.from(allRoles),
            rolesWithCounts,
            rolesToDisplay,
            totalScoutingReports
          });
          
          // Only show the section if there are roles to display
          if (rolesToDisplay.length === 0) return null;
          
          return (
            <Accordion.Item eventKey="6">
              <Accordion.Header>ROLES</Accordion.Header>
              <Accordion.Body>
                <div className={styles.rolesSection}>
                  <div className={styles.rolesHelperText}>
                    Roles (attributes, traits, etc.) we expect the player to fulfill at the projected level.
                  </div>
                  <div className={styles.rolesList}>
                    {rolesToDisplay
                      .sort((a, b) => b.adjustedCount - a.adjustedCount)
                      .map(({ role, adjustedCount, percentage }) => (
                        <div key={role} className={styles.roleRow}>
                          <div className={`${styles.roleCount} ${(role === 'Facilitator' || role === 'High Motor') ? styles.largerIcon : ''}`}>
                            {roleIcons[role] ? renderRoleIcon(roleIcons[role], role) : null}
                          </div>
                          <div className={styles.roleInfo}>
                            <div className={styles.roleName}>
                              {role} ({adjustedCount}){" "}
                              <span className={styles.rolePercentage}>
                                *tagged in {percentage}% of scouting reports
                              </span>
                            </div>
                            <div className={styles.roleExplanation}>
                              {roleExplanations[role] || ""}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          );
        })()}

        {gameLogStats.length > 0 && (
          <Accordion.Item eventKey="7">
            <Accordion.Header>STATS</Accordion.Header>
            <Accordion.Body>
              <GameLogStatsTable stats={gameLogStats} />
            </Accordion.Body>
          </Accordion.Item>
        )}

        {playerProfileData &&
          Array.from({ length: 20 }, (_, i) => playerProfileData[`video${i + 1}`]).filter(Boolean).length > 0 && (
          <Accordion.Item eventKey="8">
            <Accordion.Header>VIDEOS</Accordion.Header>
            <Accordion.Body>
              {Array.from({ length: 20 }, (_, i) => playerProfileData[`video${i + 1}`])
                .filter((url) => url)
                .map((url, idx) => {
                  const videoId = getYouTubeVideoId(url);
                  const thumbnailVariant = videoThumbVariant[idx] || "maxresdefault";
                  const thumbnailUrl = videoId
                    ? `https://i.ytimg.com/vi/${videoId}/${thumbnailVariant}.jpg`
                    : null;

                  return (
                    <div key={idx} className={styles.videoCard}>
                      {!activeVideoPlayers[idx] && thumbnailUrl ? (
                        <button
                          type="button"
                          className={styles.videoThumbnailButton}
                          onClick={() => handleEnableVideoPlayer(idx)}
                          aria-label={`Play video ${idx + 1}`}
                        >
                          <img
                            src={thumbnailUrl}
                            alt={`Video ${idx + 1} thumbnail`}
                            className={styles.videoThumbnailImage}
                            onError={() => handleVideoThumbError(idx)}
                            loading="lazy"
                          />
                          <span className={styles.videoPlayBadge} aria-hidden="true" />
                        </button>
                      ) : (
                        <div className={styles.videoPlayerFrame}>
                          <ReactPlayer url={url} width="100%" height="100%" controls playing />
                        </div>
                      )}
                    </div>
                  );
                })}
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
    </div>
  );
};
